import { useCallback, useMemo, useState } from "react";

import type { ResourceCategoryV2 } from "../fields/form-entity-select";
import type { FormulaVariableRef, ScreenFieldRef } from "../utils/data-field-type-utils";
import type { FormulaVariableFormulaPayload } from "../utils/formula-variable-payload";
import { emptyFormulaVariablePayload, normalizeVariableFormulaFromApi } from "../utils/formula-variable-payload";
import { isVariableNameTakenInRule } from "../utils/variable-naming-utils";

export type RuleVariableType = "databaseSingle" | "databaseMulti" | "simple" | "formula" | "reaction";
export type VariableDataType = "boolean" | "number" | "text" | "decimal" | "date" | "datetime" | "time";

export interface SimpleVariableInput {
	name: string;
	dataTypeId?: number;
	value?: string;
	valueLiteral?: string;
	valueDatabaseTableFieldId?: string;
	valueScreenFieldId?: string;
	valueScreenField?: ScreenFieldRef;
	valueDatabaseTableFieldName?: string;
	valueScreenFieldName?: string;
	valueSimpleVariableName?: string;
	valueSimpleVariableId?: string;
	valueFormulaVariableId?: string;
	valueFormulaVariableName?: string;
	valueDatabaseSingleRowVariableId?: string;
	valueDatabaseSingleRowVariableName?: string;
	valueGlobalConstant?: string;
	valueGlobalConstantName?: string;
	valueCategory?: ResourceCategoryV2;
}

export interface FormulaVariableInput {
	name: string;
	dataTypeId: number;
	variableFormula?: FormulaVariableFormulaPayload;
}

export interface RuleVariable {
	id: string;
	name: string;
	type: RuleVariableType;
	dataTypeId?: number;
	value?: string;
	/** Formula body for `type === "formula"` (persisted with the rule). */
	variableFormula?: FormulaVariableFormulaPayload;
	variableFormulaExpression?: string;
	source?: string;
	sourceNodeId?: string;
	recordChoice?: "first" | "all";
	databaseTableId?: string;
	valueLiteral?: string;
	valueDatabaseTableFieldId?: string;
	valueScreenFieldId?: string;
	valueScreenField?: ScreenFieldRef;
	valueDatabaseTableFieldName?: string;
	valueScreenFieldName?: string;
	valueSimpleVariableName?: string;
	valueSimpleVariableId?: string;
	valueFormulaVariableId?: string;
	valueFormulaVariableName?: string;
	valueFormulaVariable?: FormulaVariableRef;
	valueDatabaseSingleRowVariableId?: string;
	valueDatabaseSingleRowVariableName?: string;
	valueGlobalConstant?: string;
	valueGlobalConstantName?: string;
	valueCategory?: ResourceCategoryV2;
}

export interface RuleVariablesState {
	databaseSingleRow: RuleVariable[];
	databaseMultiRow: RuleVariable[];
	simple: RuleVariable[];
	formula: RuleVariable[];
	reaction: RuleVariable[];
}

const buildVariableId = (prefix: string) => `${prefix}-${crypto.randomUUID?.() ?? Math.random().toString(16).slice(2)}`;

const normalizeInitialState = (initialState: Partial<RuleVariablesState>): RuleVariablesState => ({
	databaseSingleRow: initialState.databaseSingleRow ?? [],
	databaseMultiRow: initialState.databaseMultiRow ?? [],
	simple: initialState.simple ?? [],
	formula: initialState.formula ?? [],
	reaction: initialState.reaction ?? [],
});

const useRuleVariables = (initialState: Partial<RuleVariablesState> = {}) => {
	const [variables, setVariables] = useState<RuleVariablesState>(() => normalizeInitialState(initialState));

	/**
	 * Add a simple variable to the rule variables state.
	 * @param simple - The simple variable to add.
	 * @returns The created variable or null if the name is invalid.
	 */
	const addSimpleVariable = useCallback((simple: SimpleVariableInput): RuleVariable | null => {
		const trimmedName = simple.name.trim();
		if (!trimmedName) {
			return null;
		}
		let createdVariable: RuleVariable | null = null;
		setVariables((previous) => {
			const allForUniqueness = [
				...previous.databaseSingleRow,
				...previous.databaseMultiRow,
				...previous.simple,
				...previous.formula,
				...previous.reaction,
			];
			if (isVariableNameTakenInRule(trimmedName, allForUniqueness, null)) {
				return previous;
			}

			createdVariable = {
				id: buildVariableId("simple"),
				type: "simple",
				...simple,
				name: trimmedName,
				value: simple.value ?? "",
			};

			return {
				...previous,
				simple: [...previous.simple, createdVariable],
			};
		});

		return createdVariable;
	}, []);

	/**
	 * Update a simple variable in the rule variables state.
	 * @param id - The id of the variable to update.
	 * @param updates - The updates to apply to the variable.
	 * @returns True if the variable was updated, false otherwise.
	 */
	const updateSimpleVariable = useCallback((id: string, updates: SimpleVariableInput): boolean => {
		const trimmedName = updates.name.trim();
		if (!trimmedName) {
			return false;
		}

		let success = false;
		setVariables((previous) => {
			const existingVariable = previous.simple.find((v) => v.id === id);

			if (!existingVariable) {
				return previous;
			}

			const allForUniqueness = [
				...previous.databaseSingleRow,
				...previous.databaseMultiRow,
				...previous.simple,
				...previous.formula,
				...previous.reaction,
			];
			if (isVariableNameTakenInRule(trimmedName, allForUniqueness, id)) {
				return previous;
			}

			success = true;
			return {
				...previous,
				simple: previous.simple.map((variable) =>
					variable.id === id
						? {
							...variable,
							...updates,
							name: trimmedName,
							dataTypeId: updates.dataTypeId ?? variable.dataTypeId,
							value: updates.value ?? variable.value
						  }
						: variable
				),
			};
		});

		return success;
	}, []);

	/**
	 * Remove a simple variable from the rule variables state.
	 * @param id - The id of the variable to remove.
	 */
	const removeSimpleVariable = useCallback((id: string) => {
		setVariables((previous) => ({
			...previous,
			simple: previous.simple.filter((variable) => variable.id !== id),
		}));
	}, []);

	const removeFormulaVariable = useCallback((id: string) => {
		setVariables((previous) => ({
			...previous,
			formula: previous.formula.filter((variable) => variable.id !== id),
		}));
	}, []);

	const addFormulaVariable = useCallback((formula: FormulaVariableInput): RuleVariable | null => {
		const trimmedName = formula.name.trim();
		if (!trimmedName) {
			return null;
		}

		let createdVariable: RuleVariable | null = null;
		setVariables((previous) => {
			const allForUniqueness = [
				...previous.databaseSingleRow,
				...previous.databaseMultiRow,
				...previous.simple,
				...previous.formula,
				...previous.reaction,
			];
			if (isVariableNameTakenInRule(trimmedName, allForUniqueness, null)) {
				return previous;
			}

			createdVariable = {
				id: buildVariableId("formula"),
				name: trimmedName,
				type: "formula",
				dataTypeId: formula.dataTypeId,
				variableFormula:
					formula.variableFormula ?? emptyFormulaVariablePayload(),
			};

			return {
				...previous,
				formula: [...previous.formula, createdVariable],
			};
		});

		return createdVariable;
	}, []);

	const updateFormulaVariable = useCallback((id: string, updates: FormulaVariableInput): boolean => {
		const trimmedName = updates.name.trim();
		if (!trimmedName) {
			return false;
		}

		let success = false;
		setVariables((previous) => {
			const existingVariable = previous.formula.find((v) => v.id === id);

			if (!existingVariable) {
				return previous;
			}

			const allForUniqueness = [
				...previous.databaseSingleRow,
				...previous.databaseMultiRow,
				...previous.simple,
				...previous.formula,
				...previous.reaction,
			];
			if (isVariableNameTakenInRule(trimmedName, allForUniqueness, id)) {
				return previous;
			}

			success = true;
			return {
				...previous,
				formula: previous.formula.map((variable) =>
					variable.id === id
						? {
							...variable,
							name: trimmedName,
							dataTypeId: updates.dataTypeId,
							variableFormula: normalizeVariableFormulaFromApi(
								updates.variableFormula ?? variable.variableFormula,
							),
						  }
						: variable
				),
			};
		});

		return success;
	}, []);

	const resetVariables = useCallback((newState: Partial<RuleVariablesState>) => {
		setVariables(normalizeInitialState(newState));
	}, []);

	/**
	 * Create or update a database variable tied to a query builder node.
	 * Moves the variable between single/multi buckets when recordChoice changes.
	 */
	const upsertDatabaseVariable = useCallback((input: {
		sourceNodeId: string;
		name: string;
		source: string;
		recordChoice: "first" | "all";
	}): RuleVariable => {
		let result: RuleVariable | null = null;
		setVariables((prev) => {
			const existing =
				prev.databaseSingleRow.find((v) => v.sourceNodeId === input.sourceNodeId) ??
				prev.databaseMultiRow.find((v) => v.sourceNodeId === input.sourceNodeId);

			const isSingle = input.recordChoice === "first";
			const targetType: RuleVariableType = isSingle ? "databaseSingle" : "databaseMulti";

			if (existing) {
				const updated: RuleVariable = {
					...existing,
					name: input.name,
					type: targetType,
					source: input.source,
					recordChoice: input.recordChoice,
				};
				result = updated;

				const withoutExisting = {
					...prev,
					databaseSingleRow: prev.databaseSingleRow.filter((v) => v.id !== existing.id),
					databaseMultiRow: prev.databaseMultiRow.filter((v) => v.id !== existing.id),
				};

				return isSingle
					? { ...withoutExisting, databaseSingleRow: [...withoutExisting.databaseSingleRow, updated] }
					: { ...withoutExisting, databaseMultiRow: [...withoutExisting.databaseMultiRow, updated] };
			}

			const newVar: RuleVariable = {
				id: buildVariableId(isSingle ? "dbSingle" : "dbMulti"),
				name: input.name,
				type: targetType,
				source: input.source,
				sourceNodeId: input.sourceNodeId,
				recordChoice: input.recordChoice,
			};
			result = newVar;

			return isSingle
				? { ...prev, databaseSingleRow: [...prev.databaseSingleRow, newVar] }
				: { ...prev, databaseMultiRow: [...prev.databaseMultiRow, newVar] };
		});

		return result!;
	}, []);

	const cleanOrphanedDatabaseVariables = useCallback((activeQueryNodeIds: Set<string>) => {
		setVariables((prev) => {
			const newSingle = prev.databaseSingleRow.filter((v) => !v.sourceNodeId || activeQueryNodeIds.has(v.sourceNodeId));
			const newMulti = prev.databaseMultiRow.filter((v) => !v.sourceNodeId || activeQueryNodeIds.has(v.sourceNodeId));
			if (newSingle.length === prev.databaseSingleRow.length && newMulti.length === prev.databaseMultiRow.length) {
				return prev;
			}
			return { ...prev, databaseSingleRow: newSingle, databaseMultiRow: newMulti };
		});
	}, []);

	const upsertReactionVariable = useCallback((input: {
		sourceNodeId: string;
		name: string;
	}): RuleVariable => {
		let result: RuleVariable | null = null;

		setVariables((prev) => {
			const existing = prev.reaction.find((v) => v.sourceNodeId === input.sourceNodeId);

			if (existing) {
				const updated: RuleVariable = { ...existing, name: input.name };
				result = updated;
				return {
					...prev,
					reaction: prev.reaction.map((v) => v.id === existing.id ? updated : v),
				};
			}

			const newVar: RuleVariable = {
				id: buildVariableId("reaction"),
				name: input.name,
				type: "reaction",
				sourceNodeId: input.sourceNodeId,
			};
			result = newVar;
			return { ...prev, reaction: [...prev.reaction, newVar] };
		});

		return result!;
	}, []);

	const cleanOrphanedReactionVariables = useCallback((activeReactionNodeIds: Set<string>) => {
		setVariables((prev) => {
			const filtered = prev.reaction.filter((v) => !v.sourceNodeId || activeReactionNodeIds.has(v.sourceNodeId));
			if (filtered.length === prev.reaction.length) return prev;
			return { ...prev, reaction: filtered };
		});
	}, []);

	const allVariables = useMemo<RuleVariable[]>(() => [
		...variables.databaseSingleRow,
		...variables.databaseMultiRow,
		...variables.simple,
		...variables.formula,
		...variables.reaction,
	], [variables.databaseMultiRow, variables.databaseSingleRow, variables.formula, variables.simple, variables.reaction]);

	const variableOptions = useMemo(
		() => allVariables.map((variable) => ({
			id: variable.id,
			value: variable.name,
			type: variable.type,
		})),
		[allVariables],
	);
	return {
		...variables,
		addSimpleVariable,
		updateSimpleVariable,
		removeSimpleVariable,
		addFormulaVariable,
		updateFormulaVariable,
		removeFormulaVariable,
		upsertDatabaseVariable,
		cleanOrphanedDatabaseVariables,
		upsertReactionVariable,
		cleanOrphanedReactionVariables,
		resetVariables,
		allVariables,
		variableOptions,
	};
};

export default useRuleVariables;
